/**
 * Configurable promotional banner — slim gradient ribbon at the top of the
 * page, admin-editable via /admin/setup?tab=promos. Auto-hides when there's
 * no active banner for the current page, or the user has dismissed it.
 *
 * All copy + CTA + target pages are controlled from the admin UI, so this
 * component itself is 100% content-agnostic and reusable for any future
 * campaign (Emergent Builders Contest, Black Friday, seasonal, etc.).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, ArrowRight, X, Clock, Megaphone } from "lucide-react";
import axios from "axios";

interface Banner {
  banner_id: string;
  enabled: boolean;
  headline: string;
  subline?: string;
  cta_label: string;
  cta_url: string;
  ad_label?: string | null;
  ad_url?: string | null;
  pages: string[];
  dismissible: boolean;
  style_variant: string;
  icon?: string;
  version: number;
  ends_at?: string | null;
  show_header?: boolean;
  show_middle?: boolean;
  show_bottom?: boolean;
  show_popup?: boolean;
  popup_delay_seconds?: number;
}

// Ticker hook — updates every second when < 1h remains, otherwise every minute.
function useCountdown(endsAt?: string | null): {
  label: string; urgent: boolean; ended: boolean;
} | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endsAt) return;
    const target = new Date(endsAt).getTime();
    if (isNaN(target)) return;
    let interval: any;
    // Two-stage ticker: minute cadence when far, second cadence when close.
    const tick = () => {
      setNow(Date.now());
      const remaining = target - Date.now();
      const nextRate = remaining <= 3_600_000 ? 1000 : 60_000;
      // Only restart the interval if the cadence needs to change.
      // @ts-ignore
      if (tick._rate !== nextRate) {
        if (interval) clearInterval(interval);
        // @ts-ignore
        tick._rate = nextRate;
        interval = setInterval(tick, nextRate);
      }
    };
    tick();
    return () => interval && clearInterval(interval);
  }, [endsAt]);

  if (!endsAt) return null;
  const target = new Date(endsAt).getTime();
  if (isNaN(target)) return null;
  const diff = target - now;
  if (diff <= 0) return { label: "Ended", urgent: true, ended: true };

  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1000);

  let label: string;
  if (days >= 2) label = `${days}d ${hours}h left`;
  else if (days >= 1) label = `${days}d ${hours}h ${mins}m`;
  else if (hours >= 1) label = `${hours}h ${mins}m ${String(secs).padStart(2, "0")}s`;
  else label = `${mins}:${String(secs).padStart(2, "0")}`;

  return { label, urgent: diff <= 3_600_000, ended: false };
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const promo = axios.create({ baseURL: `${BACKEND}/api` });

// Map pathnames to canonical "page" keys the admin sees in the picker.
function pageKey(pathname: string): string | null {
  if (pathname === "/" || pathname === "") return "home";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/login")) return "login";
  if (pathname.startsWith("/signup")) return "signup";
  if (pathname.startsWith("/shop")) return "marketplace";
  if (pathname.startsWith("/dashboard")) return "dashboard";
  // Gift-promotion surfaces reuse the home banner so any campaign
  // configured for /home (e.g. "Emergent Builders Contest") is visible
  // to visitors landing on the gift capture and status pages too.
  if (pathname.startsWith("/gift")) return "home";
  return null; // no banner outside these surfaces
}

function isDismissed(bannerId: string, version: number): boolean {
  try {
    const key = `sg_promo_dismissed_${bannerId}_v${version}`;
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markDismissed(bannerId: string, version: number): void {
  try {
    localStorage.setItem(`sg_promo_dismissed_${bannerId}_v${version}`, "1");
  } catch {
    /* ignore */
  }
}

const VARIANTS: Record<string, string> = {
  violet:
    "bg-[linear-gradient(90deg,#4c1d95_0%,#6d28d9_35%,#9333ea_65%,#c026d3_100%)] text-white",
  saffron:
    "bg-[linear-gradient(90deg,#7c2d12_0%,#c2410c_35%,#ea580c_70%,#f97316_100%)] text-white",
  dark: "bg-[linear-gradient(90deg,#020617_0%,#0f172a_50%,#1e293b_100%)] text-white",
  minimal: "bg-white text-slate-900 border-b border-slate-200",
};

export const PromoBanner: React.FC<{ placement?: "header" | "middle" | "bottom" | "popup" }> = ({ placement = "header" }) => {
  const location = useLocation();
  const page = useMemo(() => pageKey(location.pathname), [location.pathname]);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [variantLabel, setVariantLabel] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setHidden(false);
    setBanner(null);
    setVariantLabel(null);
    if (!page) return;
    let cancelled = false;
    promo
      .get(`/promo/active?page=${page}`)
      .then((r) => {
        if (cancelled) return;
        const b: Banner | null = r.data?.banner || null;
        const v: string | null = r.data?.variant || null;
        if (b && isDismissed(b.banner_id, b.version)) {
          setHidden(true);
          return;
        }
        setBanner(b);
        setVariantLabel(v);
      })
      .catch(() => { /* stay silent */ });
    return () => {
      cancelled = true;
    };
  }, [page]);

  const countdown = useCountdown(banner?.ends_at);
  const [popupOpen, setPopupOpen] = useState(false);
  const POPUP_KEY = banner ? `sg_promo_popup_dismissed_${banner.banner_id}_v${banner.version}` : "";

  useEffect(() => {
    if (placement !== "popup" || !banner) return;
    // Suppress if the visitor dismissed this exact version before.
    try {
      if (localStorage.getItem(POPUP_KEY) === "1") return;
    } catch { /* ignore */ }
    const delay = Math.max(0, banner.popup_delay_seconds ?? 5) * 1000;
    const t = setTimeout(() => setPopupOpen(true), delay);
    return () => clearTimeout(t);
  }, [placement, banner, POPUP_KEY]);

  if (!page || !banner || hidden) return null;

  // Placement gating — every render obeys the admin toggles.
  if (placement === "header" && banner.show_header === false) return null;
  if (placement === "middle" && !banner.show_middle) return null;
  if (placement === "bottom" && !banner.show_bottom) return null;
  if (placement === "popup" && !banner.show_popup) return null;

  const variantCls = VARIANTS[banner.style_variant] || VARIANTS.violet;

  const onClickCta = () => {
    // Fire-and-forget — never block navigation. Attach variant so the admin
    // panel can score the A/B experiment.
    const qs = variantLabel ? `?variant=${encodeURIComponent(variantLabel)}` : "";
    promo.post(`/promo/${banner.banner_id}/click${qs}`).catch(() => { /* noop */ });
  };

  const onDismiss = () => {
    markDismissed(banner.banner_id, banner.version);
    setHidden(true);
  };

  const closePopup = () => {
    try { localStorage.setItem(POPUP_KEY, "1"); } catch { /* ignore */ }
    setPopupOpen(false);
  };

  // ---- POPUP placement: centered modal desktop / bottom sheet mobile ----
  if (placement === "popup") {
    if (!popupOpen) return null;
    return (
      <div
        data-testid="promo-popup"
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-0 sm:p-6"
      >
        <div
          className="absolute inset-0 bg-black/55 backdrop-blur-sm"
          onClick={closePopup}
          data-testid="promo-popup-overlay"
        />
        <div
          className={`relative w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl ${variantCls}`}
        >
          <button
            data-testid="promo-popup-close"
            onClick={closePopup}
            aria-label="Close"
            className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors z-10"
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
          <div className="p-6 sm:p-8 pr-14 sm:pr-14">
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center ring-1 ring-white/25 shrink-0">
                <Sparkles className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-lg sm:text-xl font-bold leading-tight">{banner.headline}</div>
                {banner.subline ? <div className="text-sm opacity-90 mt-1">{banner.subline}</div> : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-5">
              {banner.ad_url && banner.ad_label ? (
                <a
                  href={banner.ad_url}
                  target={banner.ad_url.startsWith("/") ? "_self" : "_blank"}
                  rel="noopener noreferrer sponsored"
                  data-testid="promo-popup-ad"
                  className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 via-orange-500 to-amber-500 text-white text-base font-bold px-4 py-2.5 ring-2 ring-white/40 shadow-[0_4px_16px_-4px_rgba(0,0,0,0.35)] transition-transform hover:scale-[1.03] active:scale-[0.98]"
                >
                  <Megaphone className="h-5 w-5" strokeWidth={2.5} /> {banner.ad_label}
                </a>
              ) : null}
              {countdown && !countdown.ended ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white text-xs font-semibold px-3 py-2 ring-1 ring-white/25 tabular-nums">
                  <Clock className="h-3.5 w-3.5" /> Ends in <span>{countdown.label}</span>
                </span>
              ) : null}
              {banner.cta_label ? (
                <a href={banner.cta_url} target="_blank" rel="noopener noreferrer sponsored" onClick={onClickCta}
                  data-testid="promo-popup-cta"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 text-sm font-semibold px-4 py-2 ml-auto">
                  {banner.cta_label} <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Placement-specific wrapper classes for header / middle / bottom.
  const wrapperCls =
    placement === "middle"
      ? "relative w-full py-4 sm:py-6"
      : placement === "bottom"
      ? "sticky bottom-0 z-40 w-full shadow-[0_-4px_20px_rgba(0,0,0,0.15)]"
      : "relative w-full";

  return (
    <div
      data-testid={`promo-banner${placement === "header" ? "" : "-" + placement}`}
      data-promo-id={banner.banner_id}
      data-promo-placement={placement}
      className={`${wrapperCls} ${variantCls}`}
      role="region"
      aria-label="Promotion"
    >
      {/* Subtle noise / shine */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.4),transparent_50%)]"
      />
      <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-6 py-2.5">
        <div className="flex items-start sm:items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
          <div
            className="h-7 w-7 shrink-0 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25 mt-0.5 sm:mt-0"
            aria-hidden="true"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-0.5">
            <span
              className="text-[13px] sm:text-sm font-semibold tracking-tight leading-snug sm:truncate"
              data-testid="promo-banner-headline"
            >
              {banner.headline}
            </span>
            {banner.subline ? (
              <span
                className="text-[11.5px] sm:text-[12.5px] opacity-85 leading-snug sm:truncate"
                data-testid="promo-banner-subline"
              >
                {banner.subline}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap justify-start sm:justify-end pl-9 sm:pl-0">
          {banner.cta_label ? (
            <a
              href={banner.cta_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={onClickCta}
              data-testid="promo-banner-cta"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white/95 hover:bg-white dark:bg-white dark:hover:bg-white text-slate-900 dark:text-slate-900 text-[12px] sm:text-[13px] font-semibold px-3.5 py-1.5 transition-colors shadow-[0_1px_0_0_rgba(0,0,0,0.06)]"
            >
              {banner.cta_label}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </a>
          ) : null}
          {banner.ad_url && banner.ad_label ? (
            <a
              href={banner.ad_url}
              target={banner.ad_url.startsWith("/") ? "_self" : "_blank"}
              rel="noopener noreferrer sponsored"
              data-testid="promo-banner-ad"
              aria-label={banner.ad_label}
              title={banner.ad_label}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 via-orange-500 to-amber-500 text-white text-sm sm:text-base font-bold px-3.5 sm:px-4 py-2 sm:py-2.5 ring-2 ring-white/40 shadow-[0_4px_16px_-4px_rgba(0,0,0,0.35)] transition-transform hover:scale-[1.03] active:scale-[0.98]"
            >
              <Megaphone className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
              <span className="whitespace-nowrap">{banner.ad_label}</span>
            </a>
          ) : null}
          {countdown && !countdown.ended ? (
            <div
              data-testid="promo-banner-countdown"
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full backdrop-blur px-2.5 py-1 text-[11px] sm:text-[11.5px] font-semibold tabular-nums ring-1 ${
                countdown.urgent
                  ? "bg-red-500/20 text-white ring-red-300/40 animate-pulse"
                  : "bg-white/10 text-white ring-white/25"
              }`}
              aria-label={`Ends in ${countdown.label}`}
            >
              <Clock className="h-3 w-3" strokeWidth={2.5} />
              <span className="opacity-80 uppercase text-[10px] tracking-wider">Ends in</span>
              <span>{countdown.label}</span>
            </div>
          ) : null}
          {banner.dismissible ? (
            <button
              type="button"
              aria-label="Dismiss announcement"
              onClick={onDismiss}
              data-testid="promo-banner-dismiss"
              className="shrink-0 h-7 w-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
